/**
 * AppsMarketplaceSection — the App Marketplace rendered as a section
 * inside MarketplacePage. No outer `<div className="marketplace-page">`
 * wrapper; the parent owns that. Use this when you want the apps tiles
 * to sit alongside / below the workflow tiles on the same page.
 *
 * For the standalone deep-link `/marketplace/apps`, AppsMarketplacePage
 * wraps this same component with its own page wrapper.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { IonIcon, IonSpinner } from '@ionic/react';
import { rocketOutline, openOutline, alertCircle, star, starOutline, addOutline, chevronForwardOutline } from 'ionicons/icons';

import { Apps } from '../../services/apps';
import ProjectsService from '../../services/projects';
import authService from '../../services/auth';
import Credits, { CREDIT_CHANGED_EVENT, formatBalance, hoursOfRuntime } from '../../services/credits';
import ClaudeCredentialModal from '../../components/integrations/ClaudeCredentialModal';
import OpenAICredentialModal from '../../components/integrations/OpenAICredentialModal';
import Field from '../../components/atoms/Field/Field';
import Dropdown from '../../components/atoms/Dropdown/Dropdown';
import AppPricingBadge, { resolveTier } from '../../components/billing/AppPricingBadge';
import MktAlertModal from '../../components/molecules/MktAlertModal/MktAlertModal';
import InsufficientCreditsModal from '../../components/billing/InsufficientCreditsModal';
// Share the same Choose-a-Project + Create-new styles the workflow
// deploy wizard uses — so apps deploy flow looks identical.
import '../MarketplacePage/MarketplaceDeployWizardModal.css';
import '../MarketplacePage/MarketplaceDetailModal.css';
import './AppsMarketplacePage.css';

/**
 * @param {Object} props
 * @param {boolean} [props.showHeader=true]  Render the section's own h2 +
 *   subtitle. Pass false when the parent page already provides the
 *   heading (e.g. the standalone /marketplace/apps wrapper).
 * @param {boolean} [props.embedded=false]  When true (combined /marketplace
 *   page below workflows), show the top divider + extra spacing. Standalone
 *   /marketplace/apps and project-scoped routes leave this false.
 */
// Special-case labels for short / acronym categories so chips read
// nicely. Anything not in the map is title-cased.
const APPS_CATEGORY_LABELS = {
  ai: 'AI',
  ci: 'CI',
  db: 'DB',
};
function formatCategoryLabel(name) {
  if (!name) return '—';
  const lower = String(name).toLowerCase();
  if (APPS_CATEGORY_LABELS[lower]) return APPS_CATEGORY_LABELS[lower];
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

// Auto-generate a default vanity subdomain LABEL from the app + a short random
// suffix (e.g. "plane-3f9a"). Pre-filled in the deploy modal so every app gets
// a clean URL by default; the user can edit it, and the backend enforces
// uniqueness (a rare collision surfaces as a 409 the modal shows inline).
function genSubdomainLabel(entry) {
  const base = String(entry?.appType || entry?.displayName || 'app')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 24) || 'app';
  const rand = Math.random().toString(36).slice(2, 6); // 4 chars [a-z0-9]
  return `${base}-${rand}`;
}

// App icon with a letter fallback. The catalog icon can be a flaky remote
// URL (GitHub avatar CDN, etc.); on load failure we render the first-letter
// chip instead of vanishing the icon (the old onError just hid the <img>,
// leaving a blank — that's the "missing icon" symptom). Module-level so its
// identity is stable across re-renders.
const MarketplaceIcon = ({ entry, className, fallbackClassName }) => {
  const [failed, setFailed] = useState(false);
  const letter = (entry.displayName || entry.appType || '?').slice(0, 1).toUpperCase();
  if (!entry.icon || failed) {
    return <span className={fallbackClassName}>{letter}</span>;
  }
  return (
    // eslint-disable-next-line jsx-a11y/img-redundant-alt
    <img
      src={entry.icon}
      alt=""
      className={className}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
};

const AppsMarketplaceSection = ({ showHeader = true, embedded = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Keys of category groups expanded via "See more" (render all items).
  // Default surface = GPT-store-style grouped sections; the chip row
  // SCROLLS to a group (it doesn't filter), "See more" expands in place.
  const [expandedKeys, setExpandedKeys] = useState([]);

  // Deploy modal state
  const [active, setActive] = useState(null);     // catalog tile (list-shape)
  const [activeDetail, setActiveDetail] = useState(null); // full detail (with myRating)
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [pickedProject, setPickedProject] = useState('');
  const [instanceName, setInstanceName] = useState('');
  // Optional vanity subdomain LABEL (the part before .apps.zibby.dev). Empty =
  // backend assigns the random instanceId host. Lowercased + stripped to the
  // DNS-label charset as the user types.
  const [instanceSubdomain, setInstanceSubdomain] = useState('');
  // Inline error for the subdomain field — set when the backend rejects the
  // chosen subdomain (409 already-taken / 400 invalid) so the confirm modal
  // stays open and the user can pick another. Cleared on edit + modal open.
  const [subdomainError, setSubdomainError] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState(null);

  // Rating UX state (mirrors workflow modal)
  const [hoverRating, setHoverRating] = useState(0);
  const [submittingRating, setSubmittingRating] = useState(false);

  // Two-step deploy flow (mirrors the workflow detail → wizard
  // hand-off). Step 1 = info / ratings / cost / facts; step 2 = pick
  // a project + name the instance + Deploy. Project-scoped opens
  // (e.g. /projects/:id/marketplace) skip step 1's project picker but
  // still preserve the same visual two-stage flow.
  const [step, setStep] = useState(1);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);

  // Credit balance for the cost banner in the deploy modal. null = not
  // yet fetched. Reloaded on every modal-open + when the global
  // `credit-changed` event fires (post-checkout return).
  const [creditBalanceMc, setCreditBalanceMc] = useState(null);
  // Drives the "Add credits to deploy" alert modal — opened when a
  // deploy is attempted without enough balance (front-end fast path or
  // backend rejection). See handleDeploy.
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  // Drives the small "Confirm deploy" modal. Opens after the user clicks
  // Deploy and we've verified a credential exists for the picked provider.
  // The actual POST runs only after the user confirms in this modal.
  const [showConfirmDeploy, setShowConfirmDeploy] = useState(false);
  // When the user clicked Deploy but had no credential for the picked
  // provider, we open the credential paste modal AND remember to re-open
  // the confirm modal once they finish pasting. Cleared once the chain
  // either completes or is cancelled.
  const [pendingConfirmAfterCred, setPendingConfirmAfterCred] = useState(false);
  // Lowercased set of instance names already used in the target project
  // — drives real name-collision detection (the backend does NOT enforce
  // name uniqueness, so two same-named instances are otherwise allowed).
  const [existingNames, setExistingNames] = useState(() => new Set());

  // Agent provider — '' (no agent, the default placeholder), 'claude', or
  // 'codex'. Drives which BYOK credential gate the deploy modal checks, and
  // what the POST body sends. Default is the "Select agent" placeholder ('')
  // so NOTHING is pre-picked: if the user deploys without choosing, the app
  // is created WITHOUT an AI agent (no key required — POST sends
  // provider:'none'). Reset to '' on each modal open so a pick for one app
  // doesn't sticky across the next deploy.
  const [pickedProvider, setPickedProvider] = useState('');
  // Inline validation: apps that surface the agent picker (deployAgentPicker)
  // REQUIRE an agent. Set true when the user clicks Deploy without selecting
  // one, so we render a red error and block the deploy instead of silently
  // deploying with no LLM agent.
  const [agentRequiredError, setAgentRequiredError] = useState(false);

  // Resource tier the app deploys on. '' = use the catalog/app default (the
  // app's current `pricing.tier`) — we only forward `tier` when the user
  // picks something OTHER than the default, so the wire shape stays unchanged
  // for the common case. Reset on every modal open; defaulted from the active
  // entry's pricing.tier when the deploy modal opens.
  const [selectedTier, setSelectedTier] = useState('');

  // CPU architecture is no longer user-selectable — the backend
  // auto-picks ARM (Graviton) for every app unless the catalog marks it
  // x86-only. No local state needed; we don't forward `architecture`.

  // BYOK credential gate — now fully PER-PROJECT (2026-06-15 credential-
  // scoping fix). Both Claude and Codex credentials are read from the SELECTED
  // PROJECT's own AI settings (its masked `secrets.aiOAuthToken` /
  // `secrets.aiApiKey`), NOT the account-level workspace-credentials store
  // (which is being deleted). There is no account-level `claudeCred` /
  // `openaiCred` state anymore — presence is derived from `selectedProject`
  // (see `projectHasAiCred` / `projectHasOpenAiCred` below).
  const [claudeModalOpen, setClaudeModalOpen] = useState(false);
  const [openaiModalOpen, setOpenaiModalOpen] = useState(false);
  // Single-modal-at-a-time invariant: when a credential paste modal is
  // open we HIDE the deploy modal (don't unmount it — `active` stays set
  // so its picked-project / instance-name state survives) so the two
  // never stack. Flipped true right before opening a cred modal, false
  // when that cred modal closes (saved or dismissed). The deploy modal's
  // portal is gated on `!appModalHidden`.
  const [appModalHidden, setAppModalHidden] = useState(false);

  const { projectId: projectScopedId } = useParams();
  const navigate = useNavigate();
  const isAuthed = authService.isAuthenticated?.() ?? false;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await Apps.listCatalog();
        if (cancelled) return;
        // Drop `disabled: true` entries (install path known-broken — the
        // deploy endpoint 4xx's them anyway). Hiding the tile avoids a
        // click-then-fail dead end in the marketplace.
        setItems(Array.isArray(data?.items) ? data.items.filter((it) => !it.disabled) : []);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load apps catalog:', err);
        setError(err.message || 'Failed to load catalog');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // When the URL is project-scoped (/projects/:id/marketplace) we skip
  // the project-picker entirely — the user already chose. We still
  // fetch the project NAME so the modal can show which project the
  // instance lands in. Outside a project, we fetch the full list so
  // the user can pick.
  const ensureProjects = useCallback(async () => {
    if (projectScopedId) {
      // Already inside a project — pin to that id and resolve its name
      // for display. A single-project fetch would be cheaper but the
      // shared service only exposes a list call; the result is cached.
      if (pickedProject === projectScopedId && projects.length > 0) return;
      setProjectsLoading(true);
      try {
        const res = await ProjectsService.getProjects();
        const list = res?.projects || [];
        setProjects(list);
        setPickedProject(projectScopedId);
      } catch (err) {
        console.error('Failed to resolve project name:', err);
        setPickedProject(projectScopedId);
      } finally {
        setProjectsLoading(false);
      }
      return;
    }
    if (projects.length > 0 || projectsLoading) return;
    setProjectsLoading(true);
    try {
      const res = await ProjectsService.getProjects();
      const list = res?.projects || [];
      setProjects(list);
      setPickedProject(list[0]?.projectId || list[0]?.id || '');
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setProjectsLoading(false);
    }
  }, [projects.length, projectsLoading, projectScopedId, pickedProject]);

  // The currently-selected project row (from the list, which returns its
  // secrets MASKED). Source of truth for the per-project credential gate.
  const selectedProject = React.useMemo(
    () => projects.find((x) => (x.projectId || x.id) === pickedProject) || null,
    [projects, pickedProject],
  );

  // Project name lookup for the "deploying to" line shown when the
  // picker is hidden (project-scoped route).
  const pickedProjectName = selectedProject?.name || '';

  // PER-PROJECT credential gate (2026-06-15 credential-scoping fix). The
  // managed-app deploy credential now comes from the SELECTED PROJECT's own
  // AI settings — NOT the account-level workspace-credentials store. The
  // list endpoint returns secrets MASKED (e.g. "sk-a…bcd"); a non-empty
  // string means the project has that secret set. "Has cred" = the project
  // has a non-empty aiOAuthToken OR aiApiKey (covers the Claude path, which
  // accepts either an OAuth subscription token or an Anthropic API key).
  const projectHasAiCred = React.useMemo(() => {
    const s = selectedProject?.secrets || {};
    return !!((s.aiOAuthToken && String(s.aiOAuthToken).length > 0)
      || (s.aiApiKey && String(s.aiApiKey).length > 0));
  }, [selectedProject]);

  // Codex-specific per-project gate. OpenAI v1 is API-key ONLY (no OAuth
  // subscription token), so a codex deploy requires the project to carry a
  // non-empty `secrets.aiApiKey` specifically — an aiOAuthToken (a Claude
  // subscription token) does NOT satisfy codex.
  const projectHasOpenAiCred = React.useMemo(
    () => !!(selectedProject?.secrets?.aiApiKey
      && String(selectedProject.secrets.aiApiKey).length > 0),
    [selectedProject],
  );

  // Token captured from the deploy modal when the project has NO AI cred.
  // EPHEMERAL — held only long enough to flow into the deploy POST body as
  // the vendor-neutral `agentToken`. Works for BOTH providers: claude
  // (sk-ant-oat01-/sk-ant-api03-) AND codex (OpenAI sk-…); the backend's
  // deployApp validates it per the selected provider. Never persisted (not
  // to an account store, NEVER to the project settings). Cleared after the
  // deploy fires or the modal is dismissed.
  const [capturedToken, setCapturedToken] = useState(null);

  const openDeploy = useCallback(async (entry) => {
    setActive(entry);
    setActiveDetail(entry); // optimistic — show list fields immediately
    setInstanceName('');
    // Pre-fill a generated subdomain so the URL is set by default; user edits.
    setInstanceSubdomain(genSubdomainLabel(entry));
    setSubdomainError('');
    setDeployError(null);
    setHoverRating(0);
    setStep(1);
    setShowCreateForm(false);
    setNewProjectName('');
    // Logged-out → show the detail modal as a public PREVIEW (mirrors the
    // pipeline marketplace, which opens its detail modal for everyone and
    // only gates login at the deploy step). The auth gate moves to the
    // step-1 "Continue" CTA below. Skip the authed-only fetches (projects,
    // credit balance, BYOK cred status) — they'd 401 and aren't needed for
    // the preview; the optimistic list fields are enough to render step 1.
    if (!isAuthed) {
      try {
        const fresh = await Apps.getCatalogEntry(entry.appType);
        setActiveDetail(fresh);
      } catch (e) {
        /* non-fatal — optimistic list fields render the preview */
      }
      return;
    }
    // Project-scoped: pin the picked project immediately so the inline
    // instance-name + Deploy footer renders right away (no flash while
    // ensureProjects resolves the name async). The detail body + this
    // footer then show together in one modal — no Continue step.
    if (projectScopedId) setPickedProject(projectScopedId);
    ensureProjects();
    // Fetch credit balance for the cost banner. Non-fatal — if the API
    // is unreachable the banner just shows "balance unknown" without
    // blocking deploy.
    Credits.getBalance()
      .then((b) => setCreditBalanceMc(Number(b?.balanceMilliCents) || 0))
      .catch(() => setCreditBalanceMc(null));
    // Reset provider pick to the "Select agent" placeholder on every modal
    // open — sticky picks across apps would be surprising, and the default
    // is no-agent (deploy without an LLM). The user opts INTO an agent
    // explicitly per deploy.
    setPickedProvider('');
    setAgentRequiredError(false);
    // Default the resource tier to the app's catalog default (pricing.tier).
    // The dropdown only offers tiers >= the app's minimum resources, so an
    // app whose default is already its floor effectively can only upsize.
    setSelectedTier(String(entry?.pricing?.tier || '').toLowerCase());
    // Architecture is auto-resolved server-side (ARM by default) — no
    // local pick to reset.
    // Both Claude and Codex creds are PER-PROJECT now (read from the selected
    // project's masked secrets, loaded via ensureProjects) — there is no
    // account-level credential fetch on open anymore.
    // Refetch detail so we have myRating + fresh aggregates the moment
    // the modal opens. Non-fatal if it fails — list fields are enough.
    try {
      const fresh = await Apps.getCatalogEntry(entry.appType);
      setActiveDetail(fresh);
    } catch (e) {
      console.warn('Failed to refresh catalog detail:', e.message);
    }
  }, [isAuthed, navigate, ensureProjects, projectScopedId]);

  // Listen for global credit-changed events so the banner stays fresh
  // if the user tops up in another tab while the modal is open.
  useEffect(() => {
    const handler = () => {
      if (!active) return;
      Credits.getBalance()
        .then((b) => setCreditBalanceMc(Number(b?.balanceMilliCents) || 0))
        .catch(() => {});
    };
    window.addEventListener(CREDIT_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CREDIT_CHANGED_EVENT, handler);
  }, [active]);

  // Fetch the names already taken in the target project so the deploy
  // form can flag a collision before submit. Re-runs when the picked
  // project changes (project-scoped pins it immediately; global picks
  // it on step 2). Destroyed instances free their name for reuse.
  useEffect(() => {
    if (!active || !pickedProject) { setExistingNames(new Set()); return undefined; }
    let cancelled = false;
    Apps.listInstances({ projectId: pickedProject })
      .then((res) => {
        if (cancelled) return;
        // listApps returns `{ apps: [...] }` (NOT `items`); the server
        // already excludes destroyed rows, so everything returned counts
        // as a taken name.
        const list = res?.apps || res?.items || [];
        const names = new Set(
          list
            .map((it) => String(it.displayName || it.name || '').trim().toLowerCase())
            .filter(Boolean),
        );
        setExistingNames(names);
      })
      .catch(() => { if (!cancelled) setExistingNames(new Set()); });
    return () => { cancelled = true; };
  }, [active, pickedProject]);

  const closeDeploy = () => {
    if (deploying) return;
    setActive(null);
    setActiveDetail(null);
    setDeployError(null);
    setHoverRating(0);
    setStep(1);
    setShowCreateForm(false);
    setNewProjectName('');
    setAppModalHidden(false);
  };

  // 0-project users: when they reach step 2, skip the dropdown and land
  // in the create form so they can name their first project immediately.
  useEffect(() => {
    if (!active || step !== 2 || projectScopedId || projectsLoading) return;
    if (projects.length === 0 && !pickedProject && !showCreateForm) {
      setShowCreateForm(true);
    }
  }, [active, step, projectScopedId, projectsLoading, projects.length, pickedProject, showCreateForm]);

  // Create + auto-pick a new project. Mirrors MarketplaceDeployWizardModal's
  // handleCreateAndPick.
  const handleCreateAndPick = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim() || creatingProject) return;
    setCreatingProject(true);
    try {
      const created = await ProjectsService.createProject({ name: newProjectName.trim() });
      // The API may return { project: {...} } or the row directly.
      const row = created?.project || created;
      if (row?.id || row?.projectId) {
        const id = row.id || row.projectId;
        setProjects((list) => [...list, row]);
        setPickedProject(id);
        setShowCreateForm(false);
        setNewProjectName('');
      }
    } catch (err) {
      console.error('Create project failed:', err);
      setDeployError(err.message || 'Could not create project');
    } finally {
      setCreatingProject(false);
    }
  };

  // Submit a rating. Backend recomputes the META aggregate atomically;
  // the response gives us the new avg + count without a follow-up GET.
  // Also patch the corresponding tile in `items` so the list view's
  // rating chip updates immediately without re-fetching the catalog.
  const submitRating = useCallback(async (n) => {
    if (!active?.appType || submittingRating) return;
    setSubmittingRating(true);
    try {
      const res = await Apps.rate(active.appType, { rating: n });
      const next = {
        ratingAvg: Number(res.ratingAvg) || 0,
        ratingCount: Number(res.ratingCount) || 0,
        ratingHistogram: res.ratingHistogram || activeDetail?.ratingHistogram,
      };
      setActiveDetail((prev) => prev ? {
        ...prev,
        ...next,
        myRating: { rating: n, review: '', ratedAt: new Date().toISOString() },
      } : prev);
      setItems((list) => list.map((it) => (
        it.appType === active.appType ? { ...it, ...next } : it
      )));
    } catch (e) {
      console.error('Rate failed:', e);
    } finally {
      setSubmittingRating(false);
    }
  }, [active, activeDetail, submittingRating]);

  // Click handler on the row's Deploy button. Doesn't fire the POST
  // directly anymore — instead it runs the local gates (name collision,
  // credit balance, BYOK credential) and either:
  //   - opens the credential paste modal (no cred → user must paste),
  //   - opens the confirm-deploy modal (cred present → user confirms),
  // OR opens the insufficient-credits modal. The actual POST runs from
  // `performDeploy` after the confirm modal's primary button fires.
  const handleDeployClick = () => {
    if (!active || !pickedProject) return;
    // Block duplicate names in the same project (backend doesn't enforce).
    const wanted = (instanceName.trim() || active.displayName || '').trim().toLowerCase();
    if (wanted && existingNames.has(wanted)) return;
    // Fast path — if we already know the balance is short, pop the
    // top-up modal without a round-trip. When the balance hasn't
    // loaded yet (creditBalanceMc null) we skip this and let the
    // backend be the gate; the catch in performDeploy routes its
    // insufficient-credits rejection to the SAME modal.
    const tierInfo = resolveTier(active.pricing);
    const fiveMinCost = tierInfo.milliCentsPerMin * 5;
    if (creditBalanceMc !== null && creditBalanceMc < fiveMinCost) {
      setShowInsufficientCredits(true);
      return;
    }
    // Apps that surface the agent picker REQUIRE an agent. If the user clicks
    // Deploy with the placeholder still selected, block the deploy and show an
    // inline red validation error so they must pick Claude/Codex — never fall
    // through to the no-agent path for these apps.
    if (active.deployAgentPicker && !pickedProvider) {
      setAgentRequiredError(true);
      return;
    }
    // No agent picked (placeholder) → deploy WITHOUT an LLM agent. SKIP the
    // BYOK credential gate entirely (no key needed) and go straight to the
    // confirm modal; performDeploy POSTs provider:'none'. Only reachable for
    // apps WITHOUT the picker (which never require an agent).
    const noAgent = !pickedProvider;
    if (noAgent) {
      setShowConfirmDeploy(true);
      return;
    }
    const isCodex = pickedProvider === 'codex';
    // PER-PROJECT BYOK credential gate (2026-06-15 credential-scoping fix),
    // now with a VENDOR-NEUTRAL ad-hoc capture for BOTH providers. The durable
    // credential comes from the SELECTED PROJECT's AI settings; if the project
    // has none, the user can paste an ad-hoc key for THIS deploy (flowing into
    // the POST as `agentToken`, per-app/ephemeral, NEVER written to project
    // settings).
    //   - codex → project `secrets.aiApiKey` (OpenAI v1 is API-key only); OR a
    //     captured ad-hoc OpenAI key this session.
    //   - claude → project aiOAuthToken OR aiApiKey; OR a captured ad-hoc token.
    if (isCodex) {
      if (projectHasOpenAiCred || capturedToken) {
        setShowConfirmDeploy(true);
        return;
      }
      // No project OpenAI key → open the capture-only paste modal so the user
      // can supply an OpenAI key for THIS deploy. After capture, onCapture
      // re-opens the confirm modal. HIDE the deploy modal first
      // (single-modal-at-a-time invariant).
      setPendingConfirmAfterCred(true);
      setAppModalHidden(true);
      setOpenaiModalOpen(true);
      return;
    }
    if (projectHasAiCred || capturedToken) {
      setShowConfirmDeploy(true);
      return;
    }
    // Claude, project has NO cred → open the capture-only paste modal. The
    // token is CAPTURED (not persisted) and flows into the deploy POST as
    // agentToken. After capture, onCapture re-opens the confirm modal.
    // HIDE the deploy modal first (single-modal-at-a-time invariant).
    setPendingConfirmAfterCred(true);
    setAppModalHidden(true);
    setClaudeModalOpen(true);
  };

  const performDeploy = async () => {
    if (!active || !pickedProject) return;
    setShowConfirmDeploy(false);
    setDeploying(true);
    setDeployError(null);
    try {
      // Architecture is NOT forwarded — the backend auto-resolves it
      // (ARM by default, x86 only for catalog-flagged x86-only apps).
      const res = await Apps.deploy({
        appType: active.appType,
        projectId: pickedProject,
        name: instanceName || undefined,
        // Optional vanity subdomain — omitted when blank (backend assigns the
        // random instanceId host). Backend re-validates + dedupes.
        subdomain: instanceSubdomain.trim() || undefined,
        // Provider wire shape:
        //   - '' (no agent picked) → provider:'none' (deploy WITHOUT an LLM
        //     agent; backend skips the cred gate + injects no key).
        //   - 'codex' → forwarded as-is (BYOK OpenAI).
        //   - 'claude' (default) → omitted, keeping the wire shape unchanged
        //     for Claude deploys (matches every CLI/MCP request).
        provider: !pickedProvider
          ? 'none'
          : (pickedProvider !== 'claude' ? pickedProvider : undefined),
        // Resource tier — forwarded ONLY when the user picked a tier other
        // than the app's default (its catalog `pricing.tier`). Omitting it
        // keeps the deploy wire shape byte-identical to the pre-tier default
        // path (CLI/MCP), where the backend sizes to the catalog default.
        tier: (selectedTier && selectedTier !== String(active.pricing?.tier || '').toLowerCase())
          ? selectedTier
          : undefined,
        // Per-app, ephemeral ad-hoc token the user typed in the deploy modal
        // because the project had no AI cred. VENDOR-NEUTRAL — sent for BOTH
        // claude and codex via the single `agentToken` field; the backend
        // validates it per the selected provider, uses it for THIS deploy, and
        // snapshots it (encrypted) onto the APP row. It is NEVER written back
        // to project settings. Omitted entirely on the no-agent path.
        agentToken: (capturedToken && pickedProvider) ? capturedToken : undefined,
      });
      // Token (if any) has been used by the deploy + snapshotted server-side.
      // Drop the in-memory copy immediately — never keep it around.
      setCapturedToken(null);
      const instanceId = res?.instanceId;
      if (instanceId) {
        navigate(`/projects/${pickedProject}/apps/${instanceId}`);
      } else {
        closeDeploy();
      }
    } catch (err) {
      console.error('Deploy failed:', err);
      const msg = err.message || 'Deploy failed';
      const code = err?.body?.code || err?.code;
      // Insufficient-credits rejection → the dedicated "Add credits to
      // deploy" modal, NOT an inline red banner. Match the backend's
      // wording ("Insufficient credits … Need at least … runway …").
      if (/insufficient credit|need at least|top up|runway/i.test(msg)) {
        setShowInsufficientCredits(true);
      } else if (err?.status === 409 || /subdomain|already in use|already taken|reserved/i.test(msg)) {
        // Subdomain rejected (taken / invalid / reserved) → keep the confirm
        // modal open and show the reason inline so the user picks another.
        setSubdomainError(msg);
        setShowConfirmDeploy(true);
      } else if (code === 'CLAUDE_CREDENTIAL_REQUIRED') {
        // The project has no AI key (backend per-project gate). Open the
        // capture-only paste modal so the user can supply a token for THIS
        // deploy. Code-only contract (never parse the human message). Drop
        // any stale captured token; hide the deploy modal so the paste
        // modal isn't stacked behind it; re-open confirm on capture.
        setCapturedToken(null);
        setPendingConfirmAfterCred(true);
        setAppModalHidden(true);
        setClaudeModalOpen(true);
      } else if (code === 'OPENAI_CREDENTIAL_REQUIRED') {
        // Codex counterpart — the project has no OpenAI key. Open the
        // capture-only OpenAI paste modal so the user can supply a key for
        // THIS deploy (sent as the vendor-neutral `agentToken`). Code-only
        // contract. Drop any stale captured token; hide the deploy modal so
        // the paste modal isn't stacked behind it; re-open confirm on capture.
        setCapturedToken(null);
        setPendingConfirmAfterCred(true);
        setAppModalHidden(true);
        setOpenaiModalOpen(true);
      } else {
        setDeployError(msg);
      }
      setDeploying(false);
    }
  };

  // While loading, show a shimmer skeleton grid (same markup + classes as
  // the Workflow/Pipeline marketplace) instead of a blank page.
  if (loading) {
    return (
      <section
        className={`marketplace-section apps-section${embedded ? ' apps-section--embedded' : ''}`}
        aria-busy="true"
      >
        {showHeader && (
          <header className="apps-section-header">
            <h2 className="apps-section-title">App Marketplace</h2>
            <p className="apps-section-subtitle">
              One-click hosted instances of popular open-source apps. Each
              instance is private to you, with an AI ops agent for install,
              monitoring, and upgrades.
            </p>
          </header>
        )}
        <div className="marketplace-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="marketplace-card marketplace-card--skeleton" aria-hidden="true">
              <span className="marketplace-rank">{i + 1}</span>
              <div className="marketplace-card-skeleton-icon" />
              <div className="marketplace-card-skeleton-text" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  // After loading: render nothing (not even the heading) on error or when
  // the catalog endpoint is unreachable. Local dev that hasn't restarted
  // devup.sh after pulling the catalog code 404s on GET /apps/catalog —
  // better to hide the whole section than show a scary "Not Found" panel.
  if (error || items.length === 0) return null;

  // Build the chip row from the union of catalog categories. Sort by
  // count desc → ties by label asc, so the most-represented category
  // sits next to the "All" pill.
  const facets = (() => {
    const counts = new Map();
    for (const it of items) {
      const c = String(it.category || '').trim().toLowerCase();
      if (!c) continue;
      counts.set(c, (counts.get(c) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count, label: formatCategoryLabel(name) }))
      .sort((a, b) => (b.count - a.count) || a.label.localeCompare(b.label));
  })();

  // AI leads both the chip row and the grouped sections (right under
  // Trending) as the headline category; the rest keep frequency rank.
  const orderedFacets = [
    ...facets.filter((f) => f.name === 'ai'),
    ...facets.filter((f) => f.name !== 'ai'),
  ];

  // ── Grouped browse (GPT-store layout) ─────────────────────────────
  // Default surface = a "Trending" section followed by one section per
  // category, each showing the top N + a "See more" that drops into the
  // flat filtered list. Only the default state (no facet, not expanded)
  // renders grouped.
  const GROUP_SIZE = 6;
  const groups = (() => {
    const out = [];
    // Trending section hidden — deploy counts aren't real yet, and it
    // duplicated apps that also appear in the category sections below.
    // Restore by re-pushing a { key:'__trending__', label:'Trending', ... }
    // group here once deploy-rank data is real.
    for (const facet of orderedFacets) {
      const inFacet = items.filter(
        (it) => String(it.category || '').toLowerCase() === facet.name
      );
      if (inFacet.length === 0) continue;
      out.push({
        key: facet.name,
        label: facet.label,
        subtitle: null,
        all: inFacet,
      });
    }
    return out;
  })();

  // Chips are anchors, not filters — smooth-scroll the target group in.
  const scrollToGroup = (key) => {
    if (typeof document === 'undefined') return;
    // `app-` prefix so apps groups don't collide with the workflow
    // marketplace groups when both render on the combined /marketplace.
    const el = document.getElementById(`mkt-group-app-${key}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // "See more" expands one group in place (render all its items).
  const expandGroup = (key) => {
    setExpandedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  // Card renderer shared by the flat grid and the grouped sections so
  // both stay pixel-identical. idx drives the rank badge.
  const renderCard = (entry, idx) => (
    <li key={entry.appType} className="marketplace-card-li">
      <button
        type="button"
        className="marketplace-card"
        onClick={() => openDeploy(entry)}
        aria-label={`Deploy ${entry.displayName}`}
      >
        <span className="marketplace-rank">{idx + 1}</span>
        <span className="marketplace-card-icon-wrap">
          <MarketplaceIcon
            entry={entry}
            className="marketplace-card-icon"
            fallbackClassName="marketplace-card-icon marketplace-card-icon--fallback"
          />
        </span>
        <span className="marketplace-card-body">
          <span className="marketplace-card-name">
            {entry.displayName}
            <span className="apps-card-version">v{entry.appVersion}</span>
          </span>
          <span className="marketplace-card-tagline">{entry.tagline}</span>
          <span className="marketplace-card-author">
            By Zibby
            <span className="marketplace-card-author-badge">Official</span>
            {Number(entry.ratingCount) > 0 && (
              <span
                className="marketplace-card-rating"
                aria-label={`${Number(entry.ratingAvg).toFixed(1)} stars, ${entry.ratingCount} rating${entry.ratingCount === 1 ? '' : 's'}`}
              >
                <IonIcon icon={star} aria-hidden="true" />
                <span className="marketplace-card-rating-value">{Number(entry.ratingAvg).toFixed(1)}</span>
                <span className="marketplace-card-rating-count">({entry.ratingCount})</span>
              </span>
            )}
            <AppPricingBadge pricing={entry.pricing} className="apps-card-pricing" />
          </span>
        </span>
        {(() => {
          const isArm = entry.armSupported !== false;
          return (
            <span className="apps-card-arches">
              <span
                className={`apps-card-arch ${isArm ? 'apps-card-arch--arm' : 'apps-card-arch--x86'} apps-card-arch--on`}
                aria-label={isArm ? 'Runs on ARM' : 'Runs on x86'}
                title={isArm ? 'Runs on ARM (Graviton) — cheaper, same price' : 'Runs on x86'}
              >{isArm ? 'ARM' : 'x86'}</span>
            </span>
          );
        })()}
      </button>
    </li>
  );

  // Effective instance name = what the user typed, else the default the
  // backend would use (catalog displayName). Collision = that name (case-
  // insensitive) already exists in the project. `active?.` keeps this
  // safe to evaluate even when no modal is open.
  const effectiveName = (instanceName.trim() || active?.displayName || '').trim().toLowerCase();
  const nameTaken = !!effectiveName && existingNames.has(effectiveName);

  return (
    <>
      <section
        className={`marketplace-section apps-section${embedded ? ' apps-section--embedded' : ''}`}
      >
        {showHeader && (
          <header className="apps-section-header">
            <h2 className="apps-section-title">App Marketplace</h2>
            <p className="apps-section-subtitle">
              One-click hosted instances of popular open-source apps. Each
              instance is private to you, with an AI ops agent for install,
              monitoring, and upgrades.
            </p>
          </header>
        )}

        {/* Category chip row — mirrors the Workflow Marketplace chips
            (same `marketplace-chips*` classes so the styling rides
            along). Hidden when there's only one category — single-axis
            filter would be a no-op. */}
        {facets.length > 1 && (
          <div className="marketplace-chips-wrap">
            <nav className="marketplace-chips" aria-label="Filter apps">
              <button
                type="button"
                className="marketplace-chip is-active"
                onClick={() => scrollToGroup('__trending__')}
                aria-pressed={true}
              >
                All
                <span className="marketplace-chip-count">{items.length}</span>
              </button>
              {orderedFacets.map((facet) => (
                  <button
                    key={facet.name}
                    type="button"
                    className="marketplace-chip"
                    onClick={() => scrollToGroup(facet.name)}
                  >
                    {facet.label}
                    <span className="marketplace-chip-count">{facet.count}</span>
                  </button>
              ))}
            </nav>
          </div>
        )}

        <div className="marketplace-groups">
          {groups.map((g) => {
            const expanded = expandedKeys.includes(g.key);
            const shown = expanded ? g.all : g.all.slice(0, GROUP_SIZE);
            const hasMore = g.all.length > GROUP_SIZE;
            return (
              <div className="mkt-group" id={`mkt-group-app-${g.key}`} key={g.key}>
                <div className="mkt-group-head">
                  <div className="mkt-group-head-text">
                    <h3 className="mkt-group-title">{g.label}</h3>
                    {g.subtitle && <p className="mkt-group-subtitle">{g.subtitle}</p>}
                  </div>
                  {hasMore && !expanded && (
                    <button
                      type="button"
                      className="mkt-group-seemore"
                      onClick={() => expandGroup(g.key)}
                    >
                      See all {g.all.length}
                      <IonIcon icon={chevronForwardOutline} aria-hidden="true" />
                    </button>
                  )}
                </div>
                <ol className="marketplace-grid">
                  {shown.map((entry, idx) => renderCard(entry, idx))}
                </ol>
              </div>
            );
          })}
        </div>
      </section>

      {active && !appModalHidden && createPortal(
        <div className="mkt-modal-overlay" onClick={closeDeploy} role="presentation">
          <div
            className="mkt-modal"
            role="dialog"
            aria-label={`Deploy ${active.displayName}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="mkt-modal-close"
              onClick={closeDeploy}
              aria-label="Close"
              disabled={deploying}
            >×</button>

            <div className="mkt-modal-body">
              {/* Reuse the workflow-detail-modal header shape (icon
                  centered on top, title centered below) so the apps
                  modal looks like a sibling of the workflow modal. */}
              <header className="mkt-modal-header">
                {active.icon ? (
                  // eslint-disable-next-line jsx-a11y/img-redundant-alt
                  <img src={active.icon} alt="" className="mkt-modal-icon" />
                ) : (
                  <div className="mkt-modal-icon mkt-modal-icon--fallback">
                    {(active.displayName || active.appType).slice(0, 1).toUpperCase()}
                  </div>
                )}
                <h2 className="mkt-modal-title">
                  {(step === 2 && !projectScopedId) ? `Deploy ${active.displayName}` : active.displayName}
                </h2>
                {(step === 1 || projectScopedId) && (
                  <p className="mkt-modal-author">
                    By Zibby
                    <span className="mkt-modal-author-badge">Official</span>
                  </p>
                )}
                <p className="mkt-modal-tagline">
                  {(step === 2 && !projectScopedId)
                    ? "Apps live inside projects. Pick an existing project — or create a new one — and we'll deploy this instance into it."
                    : active.tagline}
                </p>

                {/* Stats row — only on step 1 (detail). Step 2 (wizard)
                    is minimal: just icon + title + tagline + choose-a-
                    project, matching the workflow deploy wizard. */}
                {(step === 1 || projectScopedId) && (() => {
                  const detail = activeDetail || active;
                  const rating = Number(detail.ratingAvg) || 0;
                  const myRatingValue = Number(detail.myRating?.rating) || 0;
                  const canRate = isAuthed;
                  const starsFilled = hoverRating || myRatingValue || Math.round(rating);
                  return (
                    <div className="mkt-modal-stats" aria-label="Stats">
                      <div className="mkt-modal-stat">
                        <div className="mkt-modal-stat-value">
                          <span className="mkt-modal-stat-number">{rating.toFixed(1)}</span>
                          <span
                            className={`mkt-modal-stat-stars ${canRate ? 'is-interactive' : ''}`}
                            role={canRate ? 'radiogroup' : undefined}
                            aria-label={canRate ? 'Rate this app' : undefined}
                            onMouseLeave={canRate ? () => setHoverRating(0) : undefined}
                          >
                            {[1, 2, 3, 4, 5].map((n) => {
                              const filled = n <= starsFilled;
                              const StarEl = canRate ? 'button' : 'span';
                              const extra = canRate ? {
                                type: 'button',
                                role: 'radio',
                                'aria-checked': n === myRatingValue,
                                'aria-label': `Rate ${n} star${n === 1 ? '' : 's'}`,
                                disabled: submittingRating,
                                onMouseEnter: () => setHoverRating(n),
                                onFocus: () => setHoverRating(n),
                                onBlur: () => setHoverRating(0),
                                onClick: () => submitRating(n),
                              } : {};
                              return (
                                <StarEl key={n} className="mkt-modal-star" {...extra}>
                                  <IonIcon icon={filled ? star : starOutline} aria-hidden="true" />
                                </StarEl>
                              );
                            })}
                          </span>
                        </div>
                        <div className="mkt-modal-stat-label">
                          {(Number(detail.ratingCount) || 0)} RATING{Number(detail.ratingCount) === 1 ? '' : 'S'}
                        </div>
                      </div>
                      <div className="mkt-modal-stat">
                        <div className="mkt-modal-stat-value mkt-modal-stat-category">
                          {active.category || '—'}
                        </div>
                        <div className="mkt-modal-stat-label">Category</div>
                      </div>
                      <div className="mkt-modal-stat">
                        <div className="mkt-modal-stat-value">v{active.appVersion}</div>
                        <div className="mkt-modal-stat-label">Version</div>
                      </div>
                    </div>
                  );
                })()}
              </header>

              {/* ── STEP 1 — detail/info view. Cost banner + heads-up
                  + facts. Footer is a single "Continue" CTA that flips
                  to step 2 (the actual deploy wizard). */}
              {(step === 1 || projectScopedId) && (() => {
                const tierInfo = resolveTier(active.pricing);
                const ratePerMin = tierInfo.milliCentsPerMin;
                const balance = creditBalanceMc;
                const hrs = balance !== null ? hoursOfRuntime(ratePerMin, balance) : null;
                const balanceStr = balance !== null ? formatBalance(balance) : '—';
                const hrsStr = hrs !== null
                  ? (hrs >= 1
                      ? `≈ ${Math.floor(hrs).toLocaleString()} hours of runtime`
                      : (hrs > 0 ? `≈ ${Math.round(hrs * 60)} min of runtime` : 'insufficient runtime'))
                  : '';
                return (
                  <>
                    <div className="apps-modal-cost-banner">
                      <strong>This app costs {tierInfo.hourly} while running.</strong>
                      {' '}Your balance: <strong>{balanceStr}</strong>
                      {hrsStr ? ` (${hrsStr})` : ''}
                    </div>

                    {active.ui?.setupHint && (
                      <p className="apps-modal-hint">
                        <strong>Heads up:</strong> {active.ui.setupHint}
                      </p>
                    )}

                    <ul className="apps-modal-facts">
                      <li><strong>Public URL</strong> <span>https://<em>{'<id>'}</em>.apps.zibby.dev</span></li>
                      <li><strong>Resources</strong> <span>{(active.resources.cpu / 1024).toFixed(2)} vCPU · {(active.resources.memory / 1024).toFixed(2)} GB RAM</span></li>
                      {active.homepage && (
                        <li>
                          <strong>Homepage</strong>
                          <span>
                            <a
                              href={active.homepage}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="apps-modal-homepage-link"
                            >
                              <IonIcon icon={openOutline} aria-hidden="true" />
                              {active.homepage.replace(/^https?:\/\//, '')}
                            </a>
                          </span>
                        </li>
                      )}
                      {active.license && (
                        <li>
                          <strong>License</strong>
                          <span>{active.license}</span>
                        </li>
                      )}
                      {active.source && (
                        <li>
                          <strong>Source</strong>
                          <span>
                            <a
                              href={active.source}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="apps-modal-homepage-link"
                            >
                              <IonIcon icon={openOutline} aria-hidden="true" />
                              {active.source.replace(/^https?:\/\//, '')}
                            </a>
                          </span>
                        </li>
                      )}
                    </ul>
                  </>
                );
              })()}

              {/* ── STEP 2 — choose a project. Same shape + classes as
                  the workflow MarketplaceDeployWizardModal. Hidden
                  when the URL is already project-scoped (we know which
                  project this app gets deployed into). */}
              {step === 2 && !projectScopedId && (
                <section className="mkt-modal-section">
                  <h3 className="mkt-modal-section-title">Choose a project</h3>

                  {projectsLoading && projects.length === 0 ? (
                    <div className="mkt-wizard-loading">
                      <IonSpinner name="crescent" />
                      <span>Loading your projects…</span>
                    </div>
                  ) : null}

                  {projects.length > 0 && !showCreateForm && (
                    <Dropdown
                      className="mkt-wizard-project-dropdown"
                      options={[
                        { key: '', label: 'Select a project…' },
                        ...projects.map((p) => ({ key: p.projectId || p.id, label: p.name })),
                      ]}
                      value={pickedProject || ''}
                      onChange={(v) => setPickedProject(v || '')}
                    />
                  )}

                  {!showCreateForm ? (
                    <button
                      type="button"
                      className="mkt-wizard-create-toggle"
                      onClick={() => {
                        setShowCreateForm(true);
                        setPickedProject('');
                      }}
                    >
                      <IonIcon icon={addOutline} aria-hidden="true" />
                      <span>Create a new project</span>
                    </button>
                  ) : (
                    <form onSubmit={handleCreateAndPick} className="mkt-wizard-create-form">
                      <label className="mkt-wizard-create-label" htmlFor="apps-wizard-new-project">
                        New project name
                      </label>
                      <div className="mkt-wizard-create-row">
                        <input
                          id="apps-wizard-new-project"
                          type="text"
                          className="mkt-wizard-create-input"
                          value={newProjectName}
                          onChange={(e) => setNewProjectName(e.target.value)}
                          placeholder="my-project"
                          autoFocus
                          disabled={creatingProject}
                          autoComplete="off"
                        />
                        <button
                          type="submit"
                          className="mkt-wizard-create-submit"
                          disabled={creatingProject || !newProjectName.trim()}
                        >
                          {creatingProject ? 'Creating…' : 'Create'}
                        </button>
                        {projects.length > 0 && (
                          <button
                            type="button"
                            className="mkt-wizard-create-cancel"
                            onClick={() => {
                              setShowCreateForm(false);
                              setNewProjectName('');
                            }}
                            disabled={creatingProject}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </form>
                  )}
                </section>
              )}

              {deployError && (
                <div className="apps-modal-error">
                  <IonIcon icon={alertCircle} aria-hidden="true" /> {deployError}
                </div>
              )}
            </div>

            {/* ── Footer transitions with `step`. Step 1 = single
                "Continue" CTA. Step 2 = instance-name field +
                Deploy button + Back link. Same `mkt-modal-name-*`
                classes as the workflow wizard footer. */}
            <footer className="mkt-modal-footer apps-modal-footer">
              {step === 1 && !projectScopedId && (
                <button
                  type="button"
                  className="mkt-modal-deploy mkt-modal-deploy--full"
                  onClick={() => {
                    // Auth gate lives here (not on tile click) so logged-out
                    // users can preview the app first — same as the pipeline
                    // marketplace. Step 2 is the project picker, which needs
                    // auth, so bounce to signup with a return path.
                    if (!isAuthed) {
                      navigate(`/signup?next=${encodeURIComponent('/marketplace/apps')}`);
                      return;
                    }
                    setStep(2);
                  }}
                >
                  Continue
                </button>
              )}

              {step === 2 && !pickedProject && (
                /* No project picked yet → full-width disabled Deploy
                   placeholder. Identical shape to MarketplaceDeployWizardModal's
                   pre-pick footer. */
                <button
                  type="button"
                  className="mkt-modal-deploy mkt-modal-deploy--full"
                  disabled
                  aria-disabled="true"
                  title="Pick or create a project first"
                >
                  Deploy
                </button>
              )}

              {(step === 2 || projectScopedId) && pickedProject && (
                /* Project picked → reveal the provider picker + instance-
                   name field + final Deploy button. Same shape as the
                   workflow wizard once it transitions into the v3 footer.
                   Project-scoped shows this inline beneath the detail
                   (no Continue step) — one modal, like the workflow
                   deploy modal. */
                <>
                  {/* CPU architecture is NO LONGER user-selectable. The
                      backend auto-picks the cheaper ARM (Graviton) for
                      every app unless the catalog marks it x86-only; same
                      price to the user either way. The old ARM/x86 radio
                      picker was removed here. */}
                  {/* The optional vanity subdomain input moved to the
                      Confirm-deploy modal (below) — it sits next to the
                      final Deploy confirmation so the URL choice is the
                      last thing the user makes before the POST fires. */}
                  <div className="mkt-modal-name-label-row">
                    <label className="mkt-modal-name-label" htmlFor="apps-deploy-name-input">
                      Instance name
                    </label>
                    <p
                      className={`mkt-modal-name-help ${nameTaken ? '' : 'is-valid'}`}
                      style={nameTaken ? { color: '#ef4444' } : undefined}
                      aria-live="polite"
                    >
                      {nameTaken
                        ? 'Name taken — pick another'
                        : (pickedProjectName ? `Available — will deploy to ${pickedProjectName}` : '')}
                    </p>
                  </div>
                  {/* Compact single-row footer — mirrors the workflow
                      MarketplaceWorkflowDeployFooter shape exactly:
                      [name input flex:1] [provider select fixed-width]
                      [Deploy button]. No cred-affordance subtext (suffix
                      confirmation moved into the post-click confirm
                      modal) and no Connect-X swap (missing cred opens
                      the paste modal directly on Deploy-click). */}
                  <div className={`mkt-modal-name-row ${nameTaken ? '' : 'is-valid'}`}>
                    <Field
                      id="apps-deploy-name-input"
                      type="text"
                      className="mkt-modal-name-input"
                      value={instanceName}
                      onChange={(e) => setInstanceName(e.target.value.slice(0, 80))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !deploying) {
                          e.preventDefault();
                          handleDeployClick();
                        }
                      }}
                      placeholder={active.displayName}
                      autoComplete="off"
                      spellCheck="false"
                      disabled={deploying}
                      status={nameTaken ? 'error' : 'ok'}
                    />
                    {/* Native <select> styled by .mkt-field--select — the
                        SAME control the workflow footer uses. Default is the
                        "Select agent" placeholder (no agent): leaving it picks
                        NO AI agent — the app deploys without an LLM (no key
                        required). Claude / Codex are opt-in (BYOK). VENDOR
                        ONLY — the concrete model is the app's runtime concern,
                        so a model label here was misleading. */}
                    {active.deployAgentPicker && (
                    <select
                      className={`mkt-field mkt-field--select${agentRequiredError ? ' mkt-field--error' : ''}`}
                      value={pickedProvider}
                      onChange={(e) => { setPickedProvider(e.target.value); setAgentRequiredError(false); }}
                      disabled={deploying}
                      aria-label="Agent provider for this app"
                      aria-invalid={agentRequiredError || undefined}
                      title={pickedProvider === 'codex' ? 'Codex' : (pickedProvider === 'claude' ? 'Claude' : 'Select agent')}
                    >
                      {/* Placeholder. For deployAgentPicker apps an agent is
                          REQUIRED — clicking Deploy with this still selected
                          shows an inline error (handleDeployClick) instead of
                          deploying without an LLM agent. */}
                      <option value="">Select agent</option>
                      {/* Vendor only — the specific model is decided by the
                          app's config at runtime, so pinning a model label
                          here (Sonnet 4.6 / GPT-5) was misleading. */}
                      <option value="claude">Claude</option>
                      <option value="codex">Codex</option>
                    </select>
                    )}
                    {(() => {
                      const tierInfo = resolveTier(active.pricing);
                      const fiveMinCost = tierInfo.milliCentsPerMin * 5;
                      const insufficient = creditBalanceMc !== null
                        && creditBalanceMc < fiveMinCost;
                      return (
                        <span className="mkt-modal-deploy-split">
                          <button
                            type="button"
                            className={`mkt-modal-deploy${deploying ? ' is-loading' : ''}`}
                            disabled={deploying || nameTaken}
                            onClick={handleDeployClick}
                            aria-busy={deploying}
                            title={nameTaken ? 'Name taken — pick another' : (insufficient ? 'Add credits to deploy' : undefined)}
                          >
                            <IonIcon icon={rocketOutline} aria-hidden="true" />
                            Deploy
                          </button>
                        </span>
                      );
                    })()}
                  </div>
                  {active.deployAgentPicker && agentRequiredError && (
                    <div className="mkt-modal-agent-error" role="alert">
                      Select an agent (Claude or Codex) — this app needs one to run.
                    </div>
                  )}
                </>
              )}
            </footer>
          </div>
        </div>,
        document.body
      )}

      <ClaudeCredentialModal
        isOpen={claudeModalOpen}
        captureOnly
        onClose={() => {
          setClaudeModalOpen(false);
          // User closed the paste modal without supplying a token — abandon
          // the queued confirm step so a later Deploy-click doesn't surprise,
          // and re-show the deploy modal (single-modal-at-a-time).
          setPendingConfirmAfterCred(false);
          setAppModalHidden(false);
        }}
        defaultKind="oauth"
        onCapture={({ token }) => {
          // Per-app, ephemeral token (NOT persisted anywhere — capture-only).
          // Stash it so performDeploy sends it as agentToken, then jump
          // straight into the confirm modal. Re-show the deploy modal first.
          setCapturedToken(token);
          setAppModalHidden(false);
          if (pendingConfirmAfterCred) {
            setPendingConfirmAfterCred(false);
            setShowConfirmDeploy(true);
          }
        }}
      />

      <OpenAICredentialModal
        isOpen={openaiModalOpen}
        projectId={pickedProject}
        captureOnly
        onClose={() => {
          // User closed the paste modal without supplying a key — abandon the
          // queued confirm step and re-show the deploy modal
          // (single-modal-at-a-time).
          setOpenaiModalOpen(false);
          setPendingConfirmAfterCred(false);
          setAppModalHidden(false);
        }}
        onCapture={({ token }) => {
          // Per-app, ephemeral OpenAI key (NOT persisted anywhere —
          // capture-only). Stash it so performDeploy sends it as the
          // vendor-neutral agentToken, then jump to the confirm modal.
          setCapturedToken(token);
          setAppModalHidden(false);
          if (pendingConfirmAfterCred) {
            setPendingConfirmAfterCred(false);
            setShowConfirmDeploy(true);
          }
        }}
      />

      {/* Insufficient-credits alert — shared with the warm-pool enable
          flow on PipelineDetailPage. The modal is parameterised by
          actionLabel / rateLabel / minRuntimeLabel and computes its body
          copy from those + the formatted balance. */}
      {(() => {
        const tierInfo = active ? resolveTier(active.pricing) : null;
        const fiveMinCost = tierInfo ? tierInfo.milliCentsPerMin * 5 : null;
        return (
          <InsufficientCreditsModal
            open={showInsufficientCredits}
            onClose={() => setShowInsufficientCredits(false)}
            title="Add credits to deploy"
            ariaLabel="Insufficient credits"
            actionLabel={active ? `deploy ${active.displayName || active.appType}` : 'deploy this app'}
            rateLabel={tierInfo?.hourly}
            minRuntimeLabel="5 minutes"
            minRequiredMilliCents={fiveMinCost}
            balanceMilliCents={creditBalanceMc}
            primaryLabel="Go to Billing"
            primaryTo="/billing"
            onPrimary={() => closeDeploy()}
            secondaryLabel="Cancel"
          />
        );
      })()}

      {/* Confirm-deploy modal — pops after the user clicks the row's
          Deploy button and a credential exists for the picked provider.
          Shows the instance + project + credential suffix the deploy
          will use. Primary fires the actual POST via performDeploy. */}
      <MktAlertModal
        open={showConfirmDeploy}
        onClose={() => setShowConfirmDeploy(false)}
        title="Confirm deploy"
        ariaLabel="Confirm deploy"
        primaryLabel="Deploy"
        primaryLoading={deploying}
        onPrimary={() => { performDeploy(); }}
        secondaryLabel={null}
      >
        {active && (() => {
          const noAgent = !pickedProvider;
          const isCodex = pickedProvider === 'codex';
          // Both providers' creds are PER-PROJECT now (read from the selected
          // project's MASKED secrets), with a vendor-neutral ad-hoc capture
          // overriding for THIS deploy:
          //   - claude → prefer the captured (just-typed, ephemeral) token's
          //     suffix; else aiOAuthToken (subscription) || aiApiKey (API key).
          //   - codex  → prefer the captured ad-hoc OpenAI key's suffix; else
          //     aiApiKey (OpenAI v1 is API-key only).
          const claudeSecret = selectedProject?.secrets?.aiOAuthToken
            || selectedProject?.secrets?.aiApiKey || '';
          const claudeMode = selectedProject?.secrets?.aiOAuthToken ? 'oauth' : 'api';
          const openaiSecret = selectedProject?.secrets?.aiApiKey || '';
          const cred = isCodex
            ? (capturedToken
              ? { suffix: capturedToken.slice(-4), mode: 'api' }
              : (openaiSecret ? { suffix: String(openaiSecret).slice(-4), mode: 'api' } : null))
            : (capturedToken
              ? { suffix: capturedToken.slice(-4), mode: capturedToken.startsWith('sk-ant-oat01-') ? 'oauth' : 'api' }
              : (claudeSecret ? { suffix: String(claudeSecret).slice(-4), mode: claudeMode } : null));
          const suffix = cred?.suffix ? `···${cred.suffix}` : null;
          // Resource-tier options come from the catalog entry's
          // `managedAppTiers`, filtered to tiers whose cpu+memory are >= the
          // app's minimum footprint (never offer below its floor). The detail
          // payload (preferred) carries them; fall back to the list shape.
          const detailEntry = activeDetail || active;
          const allTiers = Array.isArray(detailEntry?.managedAppTiers) ? detailEntry.managedAppTiers : [];
          const minCpu = detailEntry?.resources?.cpu ?? 0;
          const minMemory = detailEntry?.resources?.memory ?? 0;
          const tierOpts = allTiers.filter((t) => (t.cpu ?? 0) >= minCpu && (t.memory ?? 0) >= minMemory);
          const tierOptLabel = (t) => {
            const ram = t.ramGB != null ? `${t.ramGB} GB` : (t.memory ? `${(t.memory / 1024).toFixed(t.memory % 1024 === 0 ? 0 : 1)} GB` : '');
            const vcpu = t.vCpu != null ? `${t.vCpu} vCPU` : (t.cpu ? `${(t.cpu / 1024).toFixed(t.cpu % 1024 === 0 ? 0 : 2)} vCPU` : '');
            const price = t.priceUSD != null ? ` · ~$${t.priceUSD}/mo` : '';
            return `${t.label}${vcpu || ram ? ' · ' : ''}${vcpu}${vcpu && ram ? ' / ' : ''}${ram}${price}`;
          };
          return (
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
              {noAgent ? (
                <p style={{ margin: '0 0 16px 0', color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>
                  Deploying <strong style={{ color: '#fff' }}>without an AI agent</strong> —
                  {' '}the app installs and runs, but no LLM agent is attached (no API key needed).
                </p>
              ) : suffix && (
                <p style={{ margin: '0 0 16px 0', color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>
                  {isCodex ? (
                    <>Will use <strong style={{ color: '#fff' }}>your OpenAI API key</strong> ({suffix}).</>
                  ) : (
                    <>Will use <strong style={{ color: '#fff' }}>your Claude{' '}
                      {cred?.mode === 'oauth' ? 'subscription' : 'API key'}</strong> ({suffix}).</>
                  )}
                </p>
              )}

              {/* App URL. Pre-filled with a generated label (genSubdomainLabel)
                  so every app gets a clean URL by default; the user can edit
                  it. The backend enforces uniqueness — a taken/invalid label
                  comes back as a 409/400 and we show it inline (subdomainError)
                  while keeping this modal open so they can pick another. */}
              <div className="mkt-modal-name-label-row">
                <p
                  className="mkt-modal-name-help is-valid"
                  aria-live="polite"
                  style={subdomainError ? { color: '#ff6b6b' } : undefined}
                >
                  {subdomainError
                    || `${(instanceSubdomain.trim() || '<auto>')}.apps.zibby.dev`}
                </p>
              </div>
              <div className="mkt-modal-name-row is-valid">
                <Field
                  id="apps-deploy-subdomain-input"
                  type="text"
                  className="mkt-modal-name-input"
                  value={instanceSubdomain}
                  onChange={(e) => {
                    setSubdomainError('');
                    setInstanceSubdomain(
                      e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 63),
                    );
                  }}
                  placeholder="my-app"
                  autoComplete="off"
                  spellCheck="false"
                  status={subdomainError ? 'error' : 'ok'}
                  disabled={deploying}
                />
              </div>

              {/* Resource tier picker — only when the catalog supplies tiers
                  (and there's more than one to choose). Options are filtered
                  to >= the app's minimum footprint; the default is the app's
                  catalog tier (selectedTier, seeded on modal-open). */}
              {tierOpts.length > 1 && (
                <div style={{ marginTop: 16 }}>
                  <label
                    htmlFor="apps-deploy-tier-select"
                    style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'rgba(255,255,255,0.65)' }}
                  >
                    Plan
                  </label>
                  <select
                    id="apps-deploy-tier-select"
                    className="mkt-field mkt-field--select"
                    value={selectedTier}
                    onChange={(e) => setSelectedTier(e.target.value)}
                    disabled={deploying}
                    aria-label="Resource tier"
                  >
                    {tierOpts.map((t) => (
                      <option key={t.id} value={t.id}>{tierOptLabel(t)}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          );
        })()}
      </MktAlertModal>
    </>
  );
};

export default AppsMarketplaceSection;
